import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Folder, Star, MoreVertical, Trash2, Edit, FolderKanban } from 'lucide-react'
import { toast } from 'sonner'
import { projectsApi, customFieldsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Project, CreateProjectRequest, FieldConfiguration } from '@/types'
import { validateRegex } from '@/lib/utils'
import { usePermissions } from '@/contexts/PermissionContext'

const PROJECT_GRADIENTS = [
  // Row 1 - Warm gradients
  'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)', // Coral Sunset
  'linear-gradient(135deg, #F97316 0%, #FACC15 100%)', // Orange to Yellow
  'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)', // Golden
  'linear-gradient(135deg, #84CC16 0%, #22C55E 100%)', // Lime to Green
  'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)', // Emerald to Cyan
  'linear-gradient(135deg, #14B8A6 0%, #3B82F6 100%)', // Teal to Blue
  'linear-gradient(135deg, #06B6D4 0%, #8B5CF6 100%)', // Cyan to Violet
  'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)', // Sky to Indigo
  // Row 2 - Cool gradients
  'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)', // Blue to Violet
  'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)', // Indigo to Purple
  'linear-gradient(135deg, #8B5CF6 0%, #D946EF 100%)', // Violet to Fuchsia
  'linear-gradient(135deg, #A855F7 0%, #EC4899 100%)', // Purple to Pink
  'linear-gradient(135deg, #D946EF 0%, #F43F5E 100%)', // Fuchsia to Rose
  'linear-gradient(135deg, #EC4899 0%, #EF4444 100%)', // Pink to Red
  'linear-gradient(135deg, #F43F5E 0%, #F97316 100%)', // Rose to Orange
  'linear-gradient(135deg, #EF4444 0%, #FCD34D 100%)', // Red to Yellow
  // Row 3 - Dark/Muted gradients
  'linear-gradient(135deg, #1F2937 0%, #374151 100%)', // Dark Gray
  'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)', // Navy
  'linear-gradient(135deg, #312E81 0%, #4C1D95 100%)', // Deep Indigo to Purple
  'linear-gradient(135deg, #064E3B 0%, #065F46 100%)', // Deep Emerald
  'linear-gradient(135deg, #7C2D12 0%, #9A3412 100%)', // Deep Orange
  'linear-gradient(135deg, #831843 0%, #9D174D 100%)', // Deep Pink
  'linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)', // Deep Indigo
  'linear-gradient(135deg, #0C4A6E 0%, #0369A1 100%)', // Deep Sky
]

export default function ProjectsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null)
  const [formData, setFormData] = useState<CreateProjectRequest>({
    project_name: '',
    project_description: '',
    project_color: PROJECT_GRADIENTS[0],
    custom_fields: {},
  })
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const { data: projects = [], isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.getAll,
  })

  const { data: projectFields = [] } = useQuery({
    queryKey: ['customFields', 'PROJECT'],
    queryFn: () => customFieldsApi.getAll('PROJECT'),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateProjectRequest) => projectsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(t('messages.createSuccess'))
      setCreateDialogOpen(false)
      resetForm()
    },
    onError: (error: any) => {
      const err = error.response?.data?.message || error.message || t('messages.createError')
      const errorMessage = typeof err === 'string' ? err : String(err)
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateProjectRequest> }) =>
      projectsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(t('messages.updateSuccess'))
      setEditDialogOpen(false)
      setProjectToEdit(null)
      resetForm()
    },
    onError: (error: any) => {
      const err = error.response?.data?.message || error.message || t('messages.updateError')
      const errorMessage = typeof err === 'string' ? err : String(err)
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => projectsApi.delete(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(t('messages.deleteSuccess'))
      setDeleteDialogOpen(false)
      setProjectToDelete(null)
    },
    onError: (error: any) => {
      const err = error.response?.data?.message || error.message || t('messages.deleteError')
      const errorMessage = typeof err === 'string' ? err : String(err)
      toast.error(errorMessage)
    },
  })

  const toggleStarMutation = useMutation({
    mutationFn: ({ id, isStarred }: { id: string; isStarred: boolean }) =>
      projectsApi.update(id, { is_starred: isStarred }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const resetForm = () => {
    setFormData({
      project_name: '',
      project_description: '',
      project_color: PROJECT_GRADIENTS[0],
      custom_fields: {},
    })
    setFieldErrors({})
  }

  const validateCustomField = (field: FieldConfiguration, value: string): string | null => {
    if (field.is_mandatory && !value) {
      return `${field.field_label} is required`
    }
    if (field.regex_pattern && value && !validateRegex(value, field.regex_pattern)) {
      return `${field.field_label} must match pattern: ${field.regex_pattern}`
    }
    if (field.data_type === 'NUMBER' && value && isNaN(Number(value))) {
      return `${field.field_label} must be a number`
    }
    return null
  }

  const handleCustomFieldChange = (configId: string, value: string, field: FieldConfiguration) => {
    setFormData({
      ...formData,
      custom_fields: { ...formData.custom_fields, [configId]: value },
    })

    const error = validateCustomField(field, value)
    setFieldErrors((prev) => {
      if (error) {
        return { ...prev, [configId]: error }
      }
      const { [configId]: _, ...rest } = prev
      return rest
    })
  }

  const handleDeleteClick = (project: Project) => {
    setProjectToDelete(project)
    setDeleteDialogOpen(true)
  }

  const handleEditClick = (project: Project) => {
    setProjectToEdit(project)
    // Convert custom_fields from object with config_id keys to simple key-value pairs
    const customFieldsData: Record<string, string> = {}
    if (project.custom_fields) {
      Object.entries(project.custom_fields).forEach(([configId, field]) => {
        customFieldsData[configId] = field.field_value || ''
      })
    }
    setFormData({
      project_name: project.project_name,
      project_description: project.project_description || '',
      project_color: project.project_color,
      custom_fields: customFieldsData,
    })
    setFieldErrors({})
    setEditDialogOpen(true)
  }

  const confirmDelete = () => {
    if (projectToDelete) {
      deleteMutation.mutate(projectToDelete.project_id)
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    projectFields.forEach((field) => {
      const value = formData.custom_fields?.[field.config_id] || ''
      const error = validateCustomField(field, value)
      if (error) {
        errors[field.config_id] = error
      }
    })

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return false
    }
    return true
  }

  const handleCreate = () => {
    if (!formData.project_name.trim()) {
      toast.error(t('projects.nameRequired'))
      return
    }
    if (!validateForm()) {
      return
    }
    createMutation.mutate(formData)
  }

  const handleUpdate = () => {
    if (!formData.project_name.trim()) {
      toast.error(t('projects.nameRequired'))
      return
    }
    if (!validateForm()) {
      return
    }
    if (projectToEdit) {
      updateMutation.mutate({ id: projectToEdit.project_id, data: formData })
    }
  }

  const handleToggleStar = (project: Project) => {
    toggleStarMutation.mutate({ id: project.project_id, isStarred: !project.is_starred })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">{t('common.failedToLoad')}</div>
      </div>
    )
  }

  const starredProjects = projects.filter(p => p.is_starred)
  const otherProjects = projects.filter(p => !p.is_starred)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('projects.title')}</h1>
          <p className="text-muted-foreground">
            {t('projects.manageProjects')}
          </p>
        </div>
        {canAddEdit('PROJECTS') && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('projects.newProject')}
          </Button>
        )}
      </div>

      {projects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderKanban className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('projects.noProjectsYet')}</h3>
            <p className="text-muted-foreground text-center mb-4">
              {t('projects.createFirstProject')}
            </p>
            {canAddEdit('PROJECTS') && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('projects.createProject')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {starredProjects.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                {t('projects.starredProjects')}
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {starredProjects.map((project) => (
                  <ProjectCard
                    key={project.project_id}
                    project={project}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    onToggleStar={handleToggleStar}
                    canAddEdit={canAddEdit('PROJECTS')}
                  />
                ))}
              </div>
            </div>
          )}

          {otherProjects.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('projects.allProjects')}</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {otherProjects.map((project) => (
                  <ProjectCard
                    key={project.project_id}
                    project={project}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    onToggleStar={handleToggleStar}
                    canAddEdit={canAddEdit('PROJECTS')}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Project Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('projects.createNewProject')}</DialogTitle>
            <DialogDescription>
              {t('projects.addNewProjectDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project_name">{t('projects.projectName')} *</Label>
              <Input
                id="project_name"
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                placeholder={t('projects.enterProjectName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project_description">{t('common.description')} ({t('common.optional')})</Label>
              <Textarea
                id="project_description"
                value={formData.project_description || ''}
                onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                placeholder={t('projects.enterProjectDescription')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.color')}</Label>
              <div className="grid grid-cols-8 gap-2">
                {PROJECT_GRADIENTS.map((gradient) => (
                  <button
                    key={gradient}
                    type="button"
                    className={`w-8 h-8 rounded-md border-2 transition-all ${
                      formData.project_color === gradient ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-foreground/50' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ background: gradient }}
                    onClick={() => setFormData({ ...formData, project_color: gradient })}
                  />
                ))}
              </div>
            </div>

            {/* Custom Fields */}
            {projectFields.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <Label className="text-base font-semibold">{t('settings.customFields')}</Label>
                {projectFields.map((field) => (
                  <div key={field.config_id} className="space-y-2">
                    <Label htmlFor={`create_${field.config_id}`}>
                      {field.field_label}
                      {field.is_mandatory && ' *'}
                    </Label>
                    {field.data_type === 'DROPDOWN' ? (
                      <Select
                        value={formData.custom_fields?.[field.config_id] || ''}
                        onValueChange={(v) => handleCustomFieldChange(field.config_id, v, field)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('common.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.dropdown_options?.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={`create_${field.config_id}`}
                        type={field.data_type === 'DATE' ? 'date' : field.data_type === 'NUMBER' ? 'number' : 'text'}
                        value={formData.custom_fields?.[field.config_id] || ''}
                        onChange={(e) => handleCustomFieldChange(field.config_id, e.target.value, field)}
                        placeholder={field.regex_pattern ? `Pattern: ${field.regex_pattern}` : undefined}
                      />
                    )}
                    {fieldErrors[field.config_id] && (
                      <p className="text-sm text-destructive">{fieldErrors[field.config_id]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm() }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? t('common.loading') : t('projects.createProject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('projects.editProject')}</DialogTitle>
            <DialogDescription>
              {t('projects.updateProjectDetails')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_project_name">{t('projects.projectName')} *</Label>
              <Input
                id="edit_project_name"
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                placeholder={t('projects.enterProjectName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_project_description">{t('common.description')} ({t('common.optional')})</Label>
              <Textarea
                id="edit_project_description"
                value={formData.project_description || ''}
                onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                placeholder={t('projects.enterProjectDescription')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('projects.color')}</Label>
              <div className="grid grid-cols-8 gap-2">
                {PROJECT_GRADIENTS.map((gradient) => (
                  <button
                    key={gradient}
                    type="button"
                    className={`w-8 h-8 rounded-md border-2 transition-all ${
                      formData.project_color === gradient ? 'border-foreground scale-110 ring-2 ring-offset-2 ring-foreground/50' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ background: gradient }}
                    onClick={() => setFormData({ ...formData, project_color: gradient })}
                  />
                ))}
              </div>
            </div>

            {/* Custom Fields */}
            {projectFields.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <Label className="text-base font-semibold">{t('settings.customFields')}</Label>
                {projectFields.map((field) => (
                  <div key={field.config_id} className="space-y-2">
                    <Label htmlFor={`edit_${field.config_id}`}>
                      {field.field_label}
                      {field.is_mandatory && ' *'}
                    </Label>
                    {field.data_type === 'DROPDOWN' ? (
                      <Select
                        value={formData.custom_fields?.[field.config_id] || ''}
                        onValueChange={(v) => handleCustomFieldChange(field.config_id, v, field)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('common.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.dropdown_options?.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={`edit_${field.config_id}`}
                        type={field.data_type === 'DATE' ? 'date' : field.data_type === 'NUMBER' ? 'number' : 'text'}
                        value={formData.custom_fields?.[field.config_id] || ''}
                        onChange={(e) => handleCustomFieldChange(field.config_id, e.target.value, field)}
                        placeholder={field.regex_pattern ? `Pattern: ${field.regex_pattern}` : undefined}
                      />
                    )}
                    {fieldErrors[field.config_id] && (
                      <p className="text-sm text-destructive">{fieldErrors[field.config_id]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setProjectToEdit(null); resetForm() }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('common.loading') : t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projects.deleteProject')}</DialogTitle>
            <DialogDescription>
              {t('messages.confirmDelete')} "{projectToDelete?.project_name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? t('common.loading') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface ProjectCardProps {
  project: Project
  onEdit: (project: Project) => void
  onDelete: (project: Project) => void
  onToggleStar: (project: Project) => void
  canAddEdit: boolean
}

// Helper function to determine if a color is light or dark
function isLightColor(color: string): boolean {
  // For gradients, extract the first color
  let hexColor = color
  if (color.includes('linear-gradient')) {
    const match = color.match(/#[A-Fa-f0-9]{6}/)
    hexColor = match ? match[0] : '#000000'
  }
  const hex = hexColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  // Using relative luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5
}

function ProjectCard({ project, onEdit, onDelete, onToggleStar, canAddEdit }: ProjectCardProps) {
  const { t } = useTranslation()
  const isLight = isLightColor(project.project_color)
  const textColorClass = isLight ? 'text-gray-900' : 'text-white'
  const mutedTextClass = isLight ? 'text-gray-600' : 'text-white/70'
  const hoverBgClass = isLight ? 'hover:bg-black/10' : 'hover:bg-white/10'

  return (
    <Link to={`/projects/${project.project_id}`}>
      <Card
        className="hover:shadow-lg transition-all cursor-pointer group border-0"
        style={{ background: project.project_color }}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Folder className={`h-5 w-5 ${textColorClass}`} />
              <CardTitle className={`text-lg ${textColorClass}`}>{project.project_name}</CardTitle>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleStar(project) }}
                className={`p-1 ${hoverBgClass} rounded`}
              >
                <Star className={`h-4 w-4 ${project.is_starred ? 'fill-yellow-400 text-yellow-400' : mutedTextClass}`} />
              </button>
              {canAddEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
                    <button className={`p-1 ${hoverBgClass} rounded`}>
                      <MoreVertical className={`h-4 w-4 ${textColorClass}`} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(project) }}>
                      <Edit className="mr-2 h-4 w-4" />
                      {t('common.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={(e) => { e.stopPropagation(); onDelete(project) }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('common.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          {project.project_description && (
            <CardDescription className={`line-clamp-2 ${mutedTextClass}`}>{project.project_description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <p className={`text-sm ${mutedTextClass}`}>
            {project.board_count} {project.board_count === 1 ? t('projects.board') : t('projects.boards')}
          </p>
        </CardContent>
      </Card>
    </Link>
  )
}

